import {
  type MouseEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
} from "react";
import Swiper from "@/components/shared/Swiper";
import {
  SLIDE_IMAGE_CLASS,
  SWIPER_SLIDE,
} from "@/components/shared/swiperSlideClass";
import ArticleTitles from "@/components/shopwindow/ArticleTitles";
import RecipeSummary from "@/components/shopwindow/RecipeSummary";
import ShopWindowHints from "@/components/shopwindow/ShopWindowHints";
import { eventNames } from "@/modules/Constants";
import { articleLinkOutcome } from "@/modules/articleLink";
import { isImmersiveLandscapeSize } from "@/modules/orientationManager";
import { experienceTypes } from "@/modules/ExperienceData";
import type LinkData from "@/plugin/LinkData";
import { linkTypes } from "@/plugin/LinkData";
import ShopWindowConfig, { openingSlideType } from "@/plugin/ShopWindowConfig";
import type { ExperienceId } from "@/types/experience";
import type { ExperienceState } from "@/types/state";

const REGION = "Article Image Swiper";

type ArticleImageSwiperProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

interface SlideCache {
  slides?: ReactNode[];
  idexperience?: unknown;
  configName?: string;
  immersive?: boolean;
  index?: number;
  favorites?: string;
}

type SlideArticle = Parameters<typeof ArticleTitles>[0]["article"] & {
  urls?: { url: string; idmerchant?: unknown }[];
};

const SLIDE_CLASS = "article-swiper-slide tw:flex tw:h-full tw:flex-col";

/** The accessible name of what a tap on the slide does, or null when it does nothing. */
function slideActionLabel(
  config: ShopWindowConfig,
  experienceState: ExperienceState,
  n: number,
  article: SlideArticle,
): string | null {
  if (isImmersiveLandscapeSize()) {
    return null;
  }
  const { experienceData, favoritesData } = experienceState;
  const clusterIndex = config.clusterArticleIndexAt(experienceData, n);
  const title = article?.title;
  if (config.data.isFavorites) {
    const id = experienceData.getArticleId(
      clusterIndex.clusterName,
      clusterIndex.cindex,
      clusterIndex.index,
    ) as ExperienceId;
    // The slide only adds; a favorite already saved has nothing to do.
    if (favoritesData.isFavorite(id)) {
      return null;
    }
    return title ? `Add ${title} to favorites` : "Add to favorites";
  }
  const cta = clusterIndex.cta as LinkData | undefined;
  if (!cta) {
    return null;
  }
  if (cta.type !== linkTypes.ARTICLE) {
    return title ? `Shop ${title}` : "Shop online";
  }
  const outcome = articleLinkOutcome(article?.urls);
  if (outcome.kind === "sheet") {
    return title ? `Choose a retailer for ${title}` : "Choose a retailer";
  }
  if (outcome.kind === "none") {
    return null;
  }
  const merchant = experienceData.getMerchant(
    outcome.idmerchant as ExperienceId,
  );
  const retailer =
    merchant && typeof merchant.name === "string" ? merchant.name : null;
  if (title && retailer) {
    return `Shop ${title} at ${retailer}`;
  }
  return title ? `Shop ${title}` : "Shop online";
}

type SlideBodyProps = {
  label: string | null;
  // Only the active slide is a Tab stop: focus on an off-screen slide scrolls the container under Swiper.
  active: boolean;
  onClick: (e: MouseEvent) => void;
  children: ReactNode;
};

/** The slide content in a button when the slide acts, in a plain div when it does not. */
function SlideBody({ label, active, onClick, children }: SlideBodyProps) {
  if (label === null) {
    return <div className={SLIDE_CLASS}>{children}</div>;
  }
  return (
    <button
      type="button"
      className={SLIDE_CLASS}
      aria-label={label}
      tabIndex={active ? undefined : -1}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const PRODUCT_SLIDE = `${SWIPER_SLIDE} tw:[.portrait_.product-shop-window_&]:flex-col-reverse`;

export default function ArticleImageSwiper(props: ArticleImageSwiperProps) {
  const { config, onApplicationEvent } = props;
  // Cached slide elements call these later, so they read the props of the latest commit.
  const latest = useRef(props);
  // The slide elements are rebuilt only when the experience, the config, the active index, the favorites or the immersive layout changes.
  const cache = useRef<SlideCache>({});

  useLayoutEffect(() => {
    latest.current = props;
  });

  const onItemClick = (region: string, index: number) => {
    const { experienceState, config, onApplicationEvent } = latest.current;
    const experienceData = experienceState.experienceData;
    const favoritesData = experienceState.favoritesData;

    if (onApplicationEvent) {
      const clusterIndex = config.clusterArticleIndexAt(experienceData, index);

      if (!config.data.isFavorites) {
        if (clusterIndex && clusterIndex.cta) {
          onApplicationEvent({
            region: region,
            name: eventNames.LINK,
            clusterName: clusterIndex.clusterName,
            cindex: clusterIndex.cindex,
            index: clusterIndex.index,
            link: clusterIndex.cta,
          });
        }
      } else if (
        !favoritesData.isFavorite(
          experienceData.getArticleId(
            clusterIndex.clusterName,
            clusterIndex.cindex,
            clusterIndex.index,
          ) as ExperienceId,
        )
      ) {
        onApplicationEvent({
          name: eventNames.ADD_FAVORITE,
          region: region,
          clusterName: clusterIndex.clusterName,
          cindex: clusterIndex.cindex,
          index: clusterIndex.index,
        });
      }
    }
  };

  const onSlideClick = (e: MouseEvent, region: string, index: number) => {
    e.preventDefault();
    // The immersive landscape drawer must not open the retailer link on a slide
    // tap; only the Buy button does. isLandscape() is aspect-based and also true
    // on desktop, where tap-to-buy stays.
    if (isImmersiveLandscapeSize()) {
      return;
    }
    onItemClick(region, index);
  };

  const getSlide = (
    n: number,
    article: SlideArticle,
    imageUrl: string | null | undefined,
    showDetails: boolean,
  ) => {
    const style: { backgroundImage?: string } = {};

    if (imageUrl) {
      style.backgroundImage = `url("${imageUrl}")`;
    }

    const details =
      showDetails && article ? (
        <ArticleTitles
          experienceState={props.experienceState}
          article={article}
        />
      ) : null;

    return (
      <div className={PRODUCT_SLIDE} key={`article-slide-${n}`}>
        <SlideBody
          label={slideActionLabel(config, props.experienceState, n, article)}
          active={n === config.index}
          onClick={e => onSlideClick(e, REGION, n)}
        >
          <div
            className={`${SLIDE_IMAGE_CLASS} tw:[.product-shop-window_.articles_.swiper-slide_&]:grow tw:[.product-shop-window_.articles_.swiper-slide_&]:bg-center tw:[.product-shop-window_.articles_.swiper-slide_&]:bg-contain tw:[.product-shop-window_.articles_.swiper-slide_&]:bg-no-repeat`}
            style={style}
          />
          {details}
        </SlideBody>
      </div>
    );
  };

  const getRecipeSummarySlide = (n: number) => {
    return (
      <div className={PRODUCT_SLIDE} key={`article-slide-${n}`}>
        <RecipeSummary
          hasDescription={true}
          experienceState={props.experienceState}
        />
      </div>
    );
  };

  const getHintsSlide = (n: number) => {
    return (
      <div className={PRODUCT_SLIDE} key={`article-slide-${n}`}>
        <ShopWindowHints />
      </div>
    );
  };

  const getOpeningSlide = (n: number) => {
    const experienceData = props.experienceState.experienceData;

    if (config.openingSlideType() === openingSlideType.CUSTOM) {
      if (experienceData.type === experienceTypes.RECIPE) {
        return getRecipeSummarySlide(n);
      }
    } else if (config.openingSlideType() === openingSlideType.HINTS) {
      return getHintsSlide(n);
    }
  };

  const getSlides = () => {
    const experienceData = props.experienceState.experienceData;
    const showDetails = experienceData.styleFlags.showPanelDetails();
    const size = experienceData.getClusterSize(config.clusterName, 0);
    const state = cache.current;
    const immersive = isImmersiveLandscapeSize();
    const favorites = config.data.isFavorites
      ? props.experienceState.favoritesData.getFavorites().join(",")
      : "";

    if (
      state.slides === undefined ||
      state.immersive !== immersive ||
      state.index !== config.index ||
      state.favorites !== favorites ||
      state.idexperience !== experienceData.idexperience ||
      // biome-ignore lint/suspicious/noDoubleEquals: null and undefined both mean unset
      state.configName == undefined ||
      state.configName !== config.name
    ) {
      state.slides = [];
      if (size > 0) {
        let index = 0;

        const opening = getOpeningSlide(index);
        if (opening) {
          state.slides.push(opening);
          index++;
        }

        for (let n = 0; n < size; n++) {
          const article = experienceData.getArticle(config.clusterName, 0, n);
          const imageUrl = experienceData.getArticleImageUrl(
            config.clusterName,
            0,
            n,
          );
          state.slides.push(
            getSlide(index++, article as SlideArticle, imageUrl, showDetails),
          );
        }
      }

      state.idexperience = experienceData.idexperience;
      state.configName = config.name;
      state.immersive = immersive;
      state.index = config.index;
      state.favorites = favorites;
    }

    return state.slides;
  };

  return (
    <Swiper
      className="articles tw:grow tw:cursor-pointer tw:[.portrait_.product-shop-window_&]:order-1 tw:[.portrait_.shop-window_&]:mb-[0px]"
      index={config.index}
      hasButtons={true}
      config={{
        effect: "slide",
        pagination: {
          el: ".product-shop-window .swiper-pagination",
          clickable: true,
        },
        wrapperClass: "swiper-wrapper",
        slideClass: "swiper-slide",
      }}
      previousActive={index => (index as number) > 0}
      onDecrementItem={() =>
        onApplicationEvent({
          name: eventNames.DECREMENT_SHOP_WINDOW_INDEX,
          region: REGION,
          configName: config.name,
        })
      }
      onIncrementItem={() =>
        onApplicationEvent({
          name: eventNames.INCREMENT_SHOP_WINDOW_INDEX,
          region: REGION,
          configName: config.name,
        })
      }
      onItemChanged={index =>
        onApplicationEvent({
          name: eventNames.SHOP_WINDOW_INDEX_CHANGED,
          region: REGION,
          configName: config.name,
          index: index,
        })
      }
      onTouchStart={() =>
        onApplicationEvent({
          name: eventNames.USER_INTERACTION_START,
          region: REGION,
          configName: config.name,
        })
      }
      onTouchEnd={() =>
        onApplicationEvent({
          name: eventNames.USER_INTERACTION_END,
          region: REGION,
          configName: config.name,
        })
      }
      onTransitionStart={index =>
        onApplicationEvent({
          name: eventNames.ARTICLE_TRANSITION_START,
          region: REGION,
          configName: config.name,
          index: index,
        })
      }
      onTransitionEnd={index =>
        onApplicationEvent({
          name: eventNames.ARTICLE_TRANSITION_END,
          region: REGION,
          configName: config.name,
          index: index,
        })
      }
    >
      {getSlides()}
    </Swiper>
  );
}
